// "Can't find it? Add a role type" under a role type picker (Q77). The new
// type is usable at once by whoever adds it and reviewed by a curator after.
// Not a <form>: it sits inside the role form and the profile wizard. Close
// existing names are offered first, like AddSkill.
import { similarNames, type RoleTypeStatus } from '@remote-medium/shared';
import { cn } from 'cn';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';

import { isFieldError } from './api/errors.ts';
import { notifyError } from './api/notify.ts';
import { badgeVariants } from './components/badge.tsx';
import { Button } from './components/button.tsx';
import { Input } from './components/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/select.tsx';

export interface AddedRoleType {
  id: string;
  family: string;
  name: string;
  industries: string[];
  status: RoleTypeStatus;
}

interface Props {
  /** Families the new type can belong to (approved role_type.family values). */
  families: readonly string[];
  /** The industry chosen on the form, attached to the new type as context. */
  industry: { id: string; name: string } | null;
  onAdd: (input: {
    name: string;
    family: string;
    industryId: string | null;
    note: string | null;
  }) => Promise<AddedRoleType>;
  /** Called with the created (or existing, same-name) type to select it. */
  onAdded: (roleType: AddedRoleType) => void;
  /** Types that can be picked; close names are offered before adding. */
  existing: readonly AddedRoleType[];
}

export function AddRoleType({ families, industry, onAdd, onAdded, existing }: Props) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [family, setFamily] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The typed name turned out to be a listed type, which was picked instead.
  const [alreadyListed, setAlreadyListed] = useState<string | null>(null);

  if (!open) {
    const opener = (
      <button
        type="button"
        onClick={() => {
          setAlreadyListed(null);
          setOpen(true);
        }}
        className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Can&apos;t find it? Add a role type
      </button>
    );
    if (!alreadyListed) return opener;
    return (
      <div className="flex flex-col gap-1">
        <p role="status" className="text-xs text-muted-foreground">
          &ldquo;{alreadyListed}&rdquo; is already listed, so we picked it.
        </p>
        {opener}
      </div>
    );
  }

  const matches = similarNames(name, existing);

  function submit() {
    if (name.trim().length < 2 || !family) {
      setError('Add a name and choose the closest area.');
      return;
    }
    void add({ name: name.trim(), family, note: note.trim() || null });
  }

  // Selects `roleType` and closes the form. `typed`: added by name rather than
  // picked from the suggestions, so say so when the name was already listed.
  function done(roleType: AddedRoleType, typed: boolean) {
    onAdded(roleType);
    // A new type is always pending; approved means the name was taken.
    setAlreadyListed(typed && roleType.status === 'approved' ? roleType.name : null);
    setOpen(false);
    setName('');
    setFamily(null);
    setNote('');
  }

  async function add(input: { name: string; family: string; note: string | null }) {
    setBusy(true);
    setError(null);
    try {
      done(await onAdd({ ...input, industryId: industry?.id ?? null }), true);
    } catch (e) {
      // Only refusals of the input show here; session and server errors toast.
      if (isFieldError(e)) setError(e.message);
      else notifyError(e, 'Could not add the role type. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">Add a role type</span>
        <span className="text-xs text-muted-foreground">
          You can use it straight away
          {industry ? <> (tagged {industry.name})</> : null}. A curator reviews every new role type
          and may switch it to a close existing one; we email you if that happens.
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          placeholder="e.g. Revenue manager"
          aria-label="New role type name"
        />
        <Select
          items={[
            { label: 'Closest area', value: null },
            ...families.map((f) => ({ label: f, value: f })),
          ]}
          value={family}
          onValueChange={(v) => setFamily(v)}
        >
          <SelectTrigger className="w-full" aria-label="Closest area">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {families.map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {matches.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Already listed. Use one of these?</span>
          <div className="flex flex-wrap gap-1.5">
            {matches.map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={busy}
                onClick={() => done(m, false)}
                className={cn(badgeVariants({ variant: 'outline' }), 'cursor-pointer')}
              >
                {m.name}
                <span className="font-normal text-muted-foreground">{m.family}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={300}
        placeholder="What the role does (optional)"
        aria-label="Note for the curator"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={busy} onClick={submit}>
          <PlusIcon />
          {busy ? 'Adding…' : matches.length ? 'Add as a new role type' : 'Add and use it'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
